import * as React from 'react';
import { encodeQueryParams } from 'serialize-query-params';
import { getSSRSafeSearchString, usePreviousIfShallowEqual } from './helpers';
import { useLocationContext } from './LocationContext';
import { sharedMemoizedQueryParser } from './memoizedQueryParser';
import shallowEqual from './shallowEqual';
/**
 * Given a query parameter configuration (mapping query param name to { encode, decode }),
 * return an object with the decoded values and a setter for updating them.
 */

export var useQueryParams = function useQueryParams(paramConfigMap) {
  var _a = useLocationContext(),
      location = _a[0],
      setLocation = _a[1];

  var decodedValueCacheRef = React.useRef({}); // memoize paramConfigMap to make the API nicer for consumers.
  // otherwise we'd have to useQueryParams(useMemo(() => { foo: NumberParam }, []))

  paramConfigMap = usePreviousIfShallowEqual(paramConfigMap); // create a setter for updating multiple query params at once

  var setQuery = React.useCallback(function (changes, updateType) {
    // encode as strings for the URL
    var encodedChanges = encodeQueryParams(paramConfigMap, changes); // update the URL

    setLocation(encodedChanges, updateType);
  }, [paramConfigMap, setLocation]); // read in the raw query

  var parsedQuery = sharedMemoizedQueryParser(getSSRSafeSearchString(location)); // decode all the values if we have changes

  var decodedValues = React.useMemo(function () {
    var decodedValueCache = decodedValueCacheRef.current; // we are decoding since the parsed query changed or the param config map has changed.
    // recompute new values but only for those that changed

    var paramNames = Object.keys(paramConfigMap);
    var decodedValues = {};

    for (var _i = 0, paramNames_1 = paramNames; _i < paramNames_1.length; _i++) {
      var paramName = paramNames_1[_i];
      var paramConfig = paramConfigMap[paramName];
      var encodedValue = parsedQuery[paramName];
      var decodedValue = void 0; // re-use cached decoded value if we can, otherwise generate new

      var cacheEntry = decodedValueCache[paramName];

      if (cacheEntry != undefined && shallowEqual(cacheEntry.encodedValue, encodedValue)) {
        decodedValue = cacheEntry.decodedValue;
      } else {
        if (cacheEntry == null) {
          decodedValueCache[paramName] = {};
        }

        decodedValue = paramConfig.decode(encodedValue);
        decodedValueCache[paramName].encodedValue = encodedValue;
        decodedValueCache[paramName].decodedValue = decodedValue;
      }

      decodedValues[paramName] = decodedValue;
    }

    return decodedValues;
  }, [paramConfigMap, parsedQuery]); // no longer Partial

  return [decodedValues, setQuery];
};
export default useQueryParams;